import dayJs from "dayjs";
import _ from 'lodash';
import { routes } from '@/router';

const Common = {
  GenerateUniqueId() {
    const timestamp = (+((parseInt((1 + Math.random()) * 65536) + '') + (+new Date() + ''))).toString(36);
    const randomPart = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${randomPart}`;
  },
  /**
   * 防抖
   * @param {Function} func
   * @param {Number} wait
   * @param {Boolean} immediate 是否即時調用
   * @returns
   */
  fnDebounce: (func, wait, immediate) => {
    let timeout, args, context, timestamp, result;

    const later = function() {
      const last = +new Date() - timestamp;
      if (last < wait && last > 0) {
        timeout = setTimeout(later, wait - last);
      } else {
        timeout = null;
        if (!immediate) {
          result = func.apply(context, args);
          if (!timeout) context = args = null;
        }
      }
    };

    return function(...args) {
      context = this;
      timestamp = +new Date();
      const callNow = immediate && !timeout;
      if (!timeout) timeout = setTimeout(later, wait);
      if (callNow) {
        result = func.apply(context, args);
        context = args = null;
      }

      return result;
    };
  },
  /**
   * 判断两个值是否相等（深度比對，支持包括 Date、dayjs 在内的所有类型数据，数组，乱序数组，
   * 复杂对象，嵌套對象，对象内属性打乱，对象内数组顺序打乱的比较）
   * @param {*} object1
   * @param {*} object2
   * @returns Boolean
   */
  fnDeepEquals(object1, object2) {
    //undefined
    if (typeof object1 === 'undefined' || typeof object2 === 'undefined') {
      if (tobject1 === object2) {
        return true;
      } else {
        return false;
      }
    }

    //null
    if (object1 === null || object2 === null) {
      if (object1 === object2) {
        return true;
      } else {
        return false;
      }
    }

    //NAN
    if (object1 !== object1 && object2 !== object2) {
      return true;
    }

    //String
    if (typeof object1 === 'string' || typeof object2 === 'string') {
      if (object1 === object2) {
        return true;
      } else {
        return false;
      }
    }

    //Number
    if (typeof object1 === 'number' || typeof object2 === 'number') {
      if (object1 === object2) {
        return true;
      } else {
        return false;
      }
    }

    //Boolean
    if (typeof object1 === 'boolean' || typeof object2 === 'boolean') {
      if (object1 === object2) {
        return true;
      } else {
        return false;
      }
    }

    //Date
    if (Common.fnIsDate(object1) || Common.fnIsDate(object2)) {
      if (
        Common.fnIsDate(object1) &&
        Common.fnIsDate(object2) &&
        object1.toString() === object2.toString()
      ) {
        return true;
      } else {
        return false;
      }
    }

    //dayjs
    if (dayJs.isDayjs(object1) || dayJs.isDayjs(object2)) {
      if (
        dayJs.isDayjs(object1) &&
        dayJs.isDayjs(object2) &&
        dayJs(object1).toString() === dayJs(object2).toString()
      ) {
        return true;
      } else {
        return false;
      }
    }

    if (Array.isArray(object1) && object1.length > 0) {
      if (typeof object1[0] === 'number' || typeof object1[0] === 'string') {
        object1 = object1.sort();
      } else {
        var _objKeys1 = Object.keys(object1[0]);
        if (_objKeys1 && _objKeys1.length > 0) {
          object1 = _.sortBy(object1, o => o[_objKeys1.sort()[0]]);
        }
      }
    }
    if (Array.isArray(object2) && object2.length > 0) {
      if (typeof object2[0] === 'number' || typeof object2[0] === 'string') {
        object2 = object2.sort();
      } else {
        var _objKeys2 = Object.keys(object2[0]);
        if (_objKeys2 && _objKeys2.length > 0) {
          object2 = _.sortBy(object2, o => o[_objKeys2.sort()[0]]);
        }
      }
    }

    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];

      const areObjects = fnIsObject(val1) && fnIsObject(val2);
      if ((areObjects && !Common.fnDeepEquals(val1, val2)) || (!areObjects && val1 !== val2)) {
        return false;
      }
    }

    return true;
  },
  fnIsDate (value) {
    return value instanceof Date && !isNaN(value.getTime());
  },
  /**
   * 去除字符串內的Html標籤
   * @param {String} val
   * @returns String
   */
  fnRemoveHtml(val) {
    return val.replace(/<\/?.+?\/?>/gm, '');
  },
  /**
   * 多列排序
   * @param {Array} 對象數組
   * @param {Array} 排序對象數組：[{ name: "age", direction: "asc" }, { name: "age2", direction: "desc" }]
   * @return {Array} 排序結果
   */
  fnMultiColumnSort (array, sortColumns) {
    /**
     * Demo:
     *   let list = Common.fnMultiColumnSort(_vehicles, [
     *     { name: "is_online", direction: "desc" },
     *     { name: "is_accopen", direction: "desc" },
     *   ]);
     */

    return array.sort((a, b) => {
      for (let column of sortColumns) {
        let aValue = a[column.name];
        let bValue = b[column.name];

        if (aValue < bValue) {
          return column.direction === "asc" ? -1 : 1;
        } else if (aValue > bValue) {
          return column.direction === "asc" ? 1 : -1;
        }
      }
      return 0;
    });
  },
  /**
   * px -> vw 单位换算（根据当前环境配置的设计稿宽度换算）
   * @param {Number} px px数值
   * @returns {Number} 换算结果（vw）
   */
  fnPxToViewPort(px) {
    return Number(parseFloat((Number(px) / process.env.VUE_APP_DESIGN_WIDTH) * 100).toFixed(process.env.VUE_APP_UNIT_PRECISION).replace(/\.?0+$/, ""));
  },
  /* 根據當前路由值從所有路由配置中找到當前路由項的配置對象 */
  GetRouterItem: (path) => {
    //routes
    for (let route of routes) {
      const routePattern = route.path.replace(/:(\w+)\?/g, '?([^/]*)').replace(/:\w+/g, '([^/]+)');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(path)) {
        return route;
      }
    }
    return null;
  },
}
export default Common;

function fnIsObject(object) {
  return object != null && typeof object === 'object';
}
